import {
  Component,
  DestroyRef,
  inject,
  Injector,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { FormGroup } from '@angular/forms';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { DvDynamicFormElement, DvFormUtils, FormOf } from '@devinco/ui/legacy';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TimedMarkerMapMenuPanelComponent } from '@shared/components/timed-marker-map-menu-panel/timed-marker-map-menu-panel.component';
import { RegistryComponentNames } from '@shared/constants/registry-component-name';
import { AutoZoomFitParams, MapInput } from '@features/map/interfaces';
import {
  BehaviorSubject,
  interval,
  map,
  Observable,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { DriverPopupContentComponent } from '@shared/components/driver-popup-content/driver-popup-content.component';
import {
  CustomVehicleMapSettings,
  VehicleDto,
} from './components/vehicles-grid/interfaces';
import { VehiclesGridService } from './components/vehicles-grid/vehicles-grid.service';
import { VehiclesMapService } from './services/vehicles-map.service';
import { environment } from '@env/environment';
import { MapSetting } from '@features/map/constants/constants';
import { VtSplitterService } from '@shared/services/grid/vt-splitter.service';
import { MapId } from '@features/map/constants/map-id.enum';
import {
  DvSplitterEventsService,
  DvSplitterResizableSide,
} from '@devinco/ui/dds/splitter';
import {
  EntityFilter,
  EntityFilterDto,
} from '@shared/interfaces/vt-filter-menu-item';
import { VehiclesGridComponent } from './components/vehicles-grid/vehicles-grid.component';
import { LocalStorageItems } from '@shared/constants/local-storage-item';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserMetadataService } from '@core/services/user-metadata.service';
import { EntityFilterService } from '@shared/services/filter-area/entity-filters.service';
import { activeRecordFilter } from '@shared/constants/active-record-filter';

@Component({
  templateUrl: './vehicles-page.component.html',
  styleUrls: ['../../styles/page.scss'],
  providers: [VehiclesMapService, EntityFilterService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VehiclesPageComponent implements OnInit {
  readonly metaDataService = inject(UserMetadataService);
  private readonly splitterEvents = inject(DvSplitterEventsService);
  private api = inject(RestApiService);
  private splitterService = inject(VtSplitterService);
  private vehicleGridService = inject(VehiclesGridService);
  private vehiclesMapService = inject(VehiclesMapService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private destroyRef = inject(DestroyRef);
  private filtersService = inject(EntityFilterService);

  @ViewChild('menuPanelTemplate')
  menuPanelContent: TemplateRef<TimedMarkerMapMenuPanelComponent>;
  @ViewChild('vehiclesGrid')
  vehiclesGrid: VehiclesGridComponent;

  canAddVehicle$: Observable<boolean>;
  canSeeMap$: Observable<boolean>;
  customSettingsForm: FormGroup<FormOf<CustomVehicleMapSettings>>;
  customSettingsElements: DvDynamicFormElement[];
  availableFilters$: Observable<EntityFilter[]>;
  isFleetFilterAreaOpen = true;
  disableMapBtn = false;

  resizableSide: typeof DvSplitterResizableSide = DvSplitterResizableSide;
  readonly mapSetting = MapSetting.VehiclesPageMap;

  private markersArray$ = new BehaviorSubject<MapInput[]>([]);
  private refreshSubscription: Subscription | undefined;
  private autoZoomFit$ = new BehaviorSubject<AutoZoomFitParams>({
    doAutoFit: false,
  });
  private toggleMarkerClustering$ = new BehaviorSubject<boolean>(true);
  private availableVehicles$ = new BehaviorSubject<VehicleDto[]>([]);

  constructor() {
    const injector = inject(Injector);

    // do not register element if it's already defined
    if (!customElements.get(RegistryComponentNames.DriverPopupContent)) {
      const VtDriverMapPopupElement = createCustomElement(
        DriverPopupContentComponent,
        {
          injector,
        }
      );
      // Register the custom element with the browser.
      customElements.define(
        RegistryComponentNames.DriverPopupContent,
        VtDriverMapPopupElement
      );
    }
  }

  ngOnInit(): void {
    this.getFilters();
    this.toggleFilterAreaVisibility();
    this.canAddVehicle$ = this.permissionsService.hasPermissions([
      RolesAndPermissions.VehicleEdit,
    ]);
    this.canSeeMap$ = this.permissionsService.hasPermissions([
      RolesAndPermissions.VehicleShowInMap,
    ]);
    this.customSettingsElements =
      this.vehiclesMapService.customVehicleSettingElements;
    this.customSettingsForm = DvFormUtils.createFormGroup(
      this.customSettingsElements
    );
  }

  onCreateClick(): void {
    this.splitterEvents.isSplitterOpened$.next(true);
  }

  addFilterFn = (): void => {
    const filterModel = this.vehiclesGrid.gridApi.getFilterModel();
    this.filtersService.openAddNewFilterDialog(Entity.VehicleFilter, {
      FilterModel: filterModel,
    });
  };

  onActiveFilterChanged(dataFilterId: number): void {
    const selectedFilterData =
      this.filtersService.getSelectedFilterData(dataFilterId);
    if (selectedFilterData) {
      this.filtersService.setActiveFilter(dataFilterId);
      this.vehiclesGrid.gridApi.deselectAll();
      if (selectedFilterData.FilterModel) {
        this.vehiclesGrid.gridApi.setFilterModel(
          selectedFilterData.FilterModel
        );
      }
    }
  }

  onFilterAreaToggled(isOpen: boolean): void {
    localStorage.setItem(
      LocalStorageItems.VehicleFilterAreaToggleKey,
      String(isOpen)
    );
    this.isFleetFilterAreaOpen = isOpen;
  }

  vehiclesGridFiltersCleared(): void {
    this.filtersService.deselectActiveFilters();
  }

  onShowAllVehiclesClick(): void {
    this.disableMapBtn = true;
    this.getVehiclesForMapView().subscribe(vehicles => {
      this.markersArray$.next(
        this.vehiclesMapService.getMapMarkers(
          vehicles,
          this.customSettingsForm.value
        )
      );
      this.vehiclesMapService
        .showVehiclesOnMap(
          MapId.VehiclesMap,
          this.markersArray$,
          this.menuPanelContent,
          this.autoZoomFit$,
          this.toggleMarkerClustering$
        )
        .closed.pipe(take(1))
        .subscribe(() => {
          this.disableMapBtn = false;
          this.refreshSubscription?.unsubscribe();
        });
    });
  }

  onAutoRefreshClicked(isAutoRefreshOn: boolean): void {
    if (isAutoRefreshOn) {
      this.refreshSubscription = interval(environment.mapRefreshInterval)
        .pipe(
          switchMap(() =>
            this.getVehiclesForMapView().pipe(
              tap(resp => {
                this.markersArray$.next(
                  this.vehiclesMapService.getMapMarkers(
                    resp,
                    this.customSettingsForm.value
                  )
                );
              })
            )
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    } else {
      this.refreshSubscription?.unsubscribe();
    }
  }

  onAutoZoomFitClicked(isAutoZoomOn: boolean): void {
    this.autoZoomFit$.next({ doAutoFit: isAutoZoomOn });
  }

  onGroupMarkersClicked(isClusteringOn: boolean): void {
    this.toggleMarkerClustering$.next(isClusteringOn);
  }

  onCustomSettingsChanged(settings: CustomVehicleMapSettings): void {
    this.availableVehicles$.pipe(take(1)).subscribe(vehicles => {
      this.markersArray$.next(
        this.vehiclesMapService.getMapMarkers(vehicles, settings)
      );
    });
  }

  private toggleFilterAreaVisibility(): void {
    const filterAreaStateValue = localStorage.getItem(
      LocalStorageItems.VehicleFilterAreaToggleKey
    );
    this.isFleetFilterAreaOpen =
      filterAreaStateValue === 'true' || filterAreaStateValue === null;
  }

  private getFilters(): void {
    this.filtersService.updateFilterDataGetterFn = this.updateFilterFn;
    this.filtersService.fetchFilters(Entity.VehicleFilter);
    this.availableFilters$ = this.filtersService.availableFilters$;
  }

  private updateFilterFn = (): EntityFilterDto | undefined => {
    const activeFilterData = this.filtersService.getActiveFilter();

    if (!activeFilterData) return;
    return {
      DataFilterID: activeFilterData.DataFilterID,
      Name: activeFilterData.Name,
      FilterModel: this.vehiclesGrid.gridApi.getFilterModel(),
      VisibleToAllUsers: activeFilterData.VisibleToAllUsers,
      Editable: activeFilterData.Editable,
    };
  };

  private getVehiclesForMapView(): Observable<VehicleDto[]> {
    return this.api
      .getData<VehicleDto>({
        entity: Entity.VehicleInMap,
        fields: this.vehicleGridService
          .getColDefs()
          .map(colDef => colDef.field!),
        filterModel: {
          ...this.splitterService.agGrid.api.getFilterModel(),
          ...activeRecordFilter,
        },
        quickFilterText:
          this.splitterService.agGrid.context?.quickFilterText ||
          this.splitterService.agGrid.gridOptions?.context?.quickFilterText,
      })
      .pipe(
        map(resp => resp.data || []),
        take(1),
        tap(data => this.availableVehicles$.next(data))
      );
  }
}
